import { useState, useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { ThemeProvider } from "@/contexts/ThemeContext";
import { useAuth } from "@/hooks/useAuth";
import { useConversationList } from "@/hooks/useConversationList";
import { useMobile } from "@/hooks/useMobile";
import ConversationView from "@/components/ConversationView";
import ConversationSidebar from "@/components/ConversationSidebar";
import LandingView from "@/components/LandingView";
import BreatheView from "@/components/BreatheView";
import LoginView from "@/components/LoginView";
import RegisterView from "@/components/RegisterView";
import SettingsView from "@/components/SettingsView";
import AboutView from "@/components/AboutView";
import SlideOverMenu from "@/components/SlideOverMenu";
import MobileHistorySheet from "@/components/MobileHistorySheet";
import SessionsListView from "@/components/SessionsListView";
import ConversationReadView from "@/components/ConversationReadView";
import ReturnSheet from "@/components/ReturnSheet";
import ReflectionView from "@/components/ReflectionView";
import OnboardingView from "@/components/OnboardingView";
import AppHeader from "@/components/AppHeader";
import SubscriptionGate from "@/components/SubscriptionGate";
import TrialPaymentView from "@/components/TrialPaymentView";
import TrialConfirmView from "@/components/TrialConfirmView";
import LandingPageView from "@/components/LandingPageView";
import BetaSurvey from "@/components/BetaSurvey";
import AdminDashboard from "@/components/AdminDashboard";
import DeleteAccountView from "@/components/DeleteAccountView";

const Main = () => {
  // --- Hooks ---
  const auth = useAuth();
  const convList = useConversationList();
  const isMobile = useMobile();

  // --- Navigation state ---
  const [view, setView] = useState("landing");
  const [currentConversationId, setCurrentConversationId] = useState(null);
  const [readConversationId, setReadConversationId] = useState(null);
  const [returnOrigin, setReturnOrigin] = useState(null);
  const [reflectionConversationId, setReflectionConversationId] = useState(null);

  // --- UI overlay state ---
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [mobileHistoryOpen, setMobileHistoryOpen] = useState(false);
  const [returnSheetOpen, setReturnSheetOpen] = useState(false);
  const [showBetaSurvey, setShowBetaSurvey] = useState(false);
  const [showPayment, setShowPayment] = useState(false);
  const [showTrialConfirm, setShowTrialConfirm] = useState(false);

  // --- Bootstrap ---
  useEffect(() => {
    const status = auth.checkInitialAuth();
    if (status === 'authenticated') {
      convList.loadConversations();
    } else if (status === 'unauthenticated') {
      setView('landing_page');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // --- Actions ---
  const startFirstConversation = async () => {
    const token = localStorage.getItem('auth_token');
    const headers = token ? { Authorization: `Bearer ${token}` } : {};
    const newConv = await convList.createConversation();
    if (newConv) {
      try {
        await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/conversations/${newConv.id}/init`, {
          method: 'POST',
          headers,
        });
      } catch (e) {
        console.error('Opening message failed:', e);
      }
      setCurrentConversationId(newConv.id);
      convList.loadConversations();
      setView('conversation');
    }
  };

  const startNewConversation = async () => {
    const newConv = await convList.createConversation();
    if (newConv) {
      setCurrentConversationId(newConv.id);
      setSidebarOpen(false);
      setView("conversation");
    }
  };

  const handleDeleteConversation = async (conversationId) => {
    const remaining = convList.conversations.filter(c => c.id !== conversationId);
    await convList.deleteConversation(conversationId);

    if (currentConversationId === conversationId) {
      if (remaining.length > 0) {
        setCurrentConversationId(remaining[0].id);
      } else {
        setCurrentConversationId(null);
        setView("landing");
      }
    }
  };

  const handleNavigate = (destination) => {
    if (destination === 'conversation') {
      if (convList.conversations.length > 0) {
        setCurrentConversationId(convList.conversations[0].id);
        setView('conversation');
      } else {
        startNewConversation();
      }
    } else if (destination === 'login' || destination === 'register') {
      auth.logout();
      setView(destination);
    } else {
      setView(destination);
    }
  };

  // --- Auth gates (early returns) ---
  // Wait for checkInitialAuth() to complete before rendering anything.
  // Without this, the first render always has isAuthenticated=false (useState default),
  // then the useEffect fires, finds a token, sets isAuthenticated=true, and re-renders —
  // causing the SubscriptionGate to flash for returning users before auth is confirmed.
  if (!auth.isInitialized) return null;

  if (auth.isAuthenticated && showPayment) {
    return (
      <TrialPaymentView
        onSuccess={() => { setShowPayment(false); setShowTrialConfirm(true); }}
      />
    );
  }

  if (auth.isAuthenticated && showTrialConfirm) {
    return (
      <TrialConfirmView
        onComplete={() => {
          setShowTrialConfirm(false);
          auth.completeOnboarding();
          convList.loadConversations();
          setView('landing');
        }}
      />
    );
  }

  if (auth.isAuthenticated && auth.showOnboarding) {
    return (
      <OnboardingView
        onComplete={() => {
          auth.completeOnboarding();
          setView('landing');
          convList.loadConversations();
        }}
      />
    );
  }

  if (!auth.isAuthenticated) {
    if (view === 'register') {
      return <RegisterView onRegister={() => { auth.register(); setShowTrialConfirm(true); }} onNavigate={handleNavigate} />;
    }
    // Login screen
    if (view === 'login') {
      return (
        <LoginView
          onLogin={() => {
            auth.login();
            setView('landing');
            convList.loadConversations();
          }}
          onNavigate={handleNavigate}
        />
      );
    }
    // About page (unauthenticated)
    if (view === 'about') {
      return <AboutView onBack={() => setView('landing_page')} />;
    }
    // Default: landing page
    return (
      <LandingPageView
        onBeginTrial={() => setView('register')}
        onSignIn={() => setView('login')}
        onNavigate={setView}
      />
    );
  }

  if (auth.isAuthenticated && view === 'delete-account') {
    return (
      <DeleteAccountView
        onDeleted={() => { auth.logout(); setView('landing_page'); }}
        onBack={() => setView('landing')}
      />
    );
  }

  if (showBetaSurvey) {
    return (
      <BetaSurvey
        onComplete={() => { setShowBetaSurvey(false); setView('landing'); }}
        onRemindLater={() => { setShowBetaSurvey(false); setView('landing'); }}
      />
    );
  }

  if (view === 'settings') {
    return <SettingsView onBack={() => setView('landing')} />;
  }

  if (view === 'about') {
    return (
      <AboutView
        onBack={() => setView('landing')}
        onShowOnboarding={() => auth.triggerOnboarding()}
      />
    );
  }

  // --- Header visibility: hidden on landing & conversation ---
  const showHeader = view !== 'landing' && view !== 'conversation';

  return (
    <SubscriptionGate onSignOut={() => { auth.logout(); setView('login'); }} onUpgrade={() => setShowPayment(true)} onDeleteAccount={() => setView('delete-account')}>
    <div className="min-h-screen bg-primary flex">
      {/* Sidebar — desktop only, hidden on landing & conversation */}
      {showHeader && !isMobile && (
        <ConversationSidebar
          conversations={convList.conversations}
          currentConversationId={currentConversationId}
          onSelectConversation={(id) => {
            setCurrentConversationId(id);
            setView("conversation");
            setSidebarOpen(false);
          }}
          onNewConversation={startNewConversation}
          onDeleteConversation={handleDeleteConversation}
          isOpen={sidebarOpen}
          onClose={() => setSidebarOpen(false)}
        />
      )}

      <div className="flex-1 flex flex-col">
        {showHeader && (
          <AppHeader
            onToggleSidebar={() => setSidebarOpen(prev => !prev)}
            onOpenMenu={() => setMenuOpen(true)}
          />
        )}

        <main className="flex-1 overflow-hidden flex flex-col">
          {/* Landing */}
          {view === "landing" && (
            <LandingView
              onStartTalking={() => startNewConversation()}
              onBreathe={() => setView("breathe")}
              recentConversation={convList.conversations[0]}
              onContinue={() => setReturnSheetOpen(true)}
              onOpenHistory={() => setMobileHistoryOpen(true)}
              hasConversations={convList.conversations.length > 0}
              onMenuOpen={() => setMenuOpen(true)}
              onOpenSurvey={() => setShowBetaSurvey(true)}
            />
          )}

          {/* Sessions list */}
          {view === "sessions" && (
            <SessionsListView
              conversations={convList.conversations}
              onSelectSession={(id) => {
                setReturnOrigin('sessions');
                setReadConversationId(id);
                setView('reading');
              }}
              onDeleteSession={handleDeleteConversation}
              onBack={() => setView('landing')}
            />
          )}

          {/* Read-only conversation */}
          {view === "reading" && readConversationId && (
            <ConversationReadView
              conversationId={readConversationId}
              onBack={() => {
                setView(returnOrigin === 'sessions' ? 'sessions' : 'landing');
                setReturnOrigin(null);
              }}
              onContinue={() => {
                setCurrentConversationId(readConversationId);
                setReturnOrigin(null);
                setView('conversation');
              }}
            />
          )}

          {/* Return sheet (bottom sheet from landing) */}
          {returnSheetOpen && view === "landing" && (
            <ReturnSheet
              conversations={convList.conversations}
              onSelect={(id) => {
                setReturnSheetOpen(false);
                setReturnOrigin('sheet');
                setReadConversationId(id);
                setView('reading');
              }}
              onClose={() => setReturnSheetOpen(false)}
            />
          )}

          {/* Content area */}
          <div className="flex-1 overflow-hidden">
            {view === "conversation" && currentConversationId && (
              <ConversationView
                conversationId={currentConversationId}
                onConversationUpdate={convList.loadConversations}
                onMenuOpen={() => setMenuOpen(true)}
                onClose={() => {
                  const count = convList.conversations.length;
                  const closingId = currentConversationId;
                  setCurrentConversationId(null);

                  if (count >= 5 && count <= 10) {
                    setReflectionConversationId(closingId);
                    setView('reflection');
                  } else {
                    setView('landing');
                  }
                }}
              />
            )}

            {view === "breathe" && (
              <BreatheView
                voiceGuided={false}
                onComplete={() => setView(currentConversationId ? "conversation" : "landing")}
              />
            )}
          </div>

          {/* Reflection */}
          {view === "reflection" && reflectionConversationId && (
            <ReflectionView
              conversationId={reflectionConversationId}
              onDone={() => {
                setReflectionConversationId(null);
                setView('landing');
                convList.loadConversations();
              }}
            />
          )}
        </main>
      </div>

      {/* Mobile history bottom sheet */}
      <MobileHistorySheet
        isOpen={mobileHistoryOpen}
        onClose={() => setMobileHistoryOpen(false)}
        conversations={convList.conversations}
        onSelectConversation={(id) => {
          setCurrentConversationId(id);
          setView('conversation');
        }}
        onNewConversation={startNewConversation}
      />

      <SlideOverMenu
        isOpen={menuOpen}
        onClose={() => setMenuOpen(false)}
        onNavigate={handleNavigate}
        currentView={view}
      />
    </div>
    </SubscriptionGate>
  );
};

function App() {
  return (
    <ThemeProvider>
      <div className="App">
        <BrowserRouter>
          <Routes>
            <Route path="/" element={<Main />} />
            <Route path="/admin" element={<AdminDashboard />} />
          </Routes>
        </BrowserRouter>
      </div>
    </ThemeProvider>
  );
}

export default App;
